<template>
  <header class="AdminUINavbar">
    <nav class="AdminUINavbar__content">
      <div class="AdminUINavbar__left-side">
        <button
          v-if="!isDesktop && userInfo"
          v-ripple
          class="AdminUINavbar__left-side__toggle-button"
          data-testid="admin-ui-navbar-toggle-sidebar-button"
          aria-label="Toggle sidebar"
          @click="toggleSidebar"
        >
          <Icon name="mdi:menu" size="20" :style="{ color: 'white' }" />
        </button>
        <NuxtLink to="/admin" class="AdminUINavbar__left-side__logo-link">
          <div v-if="(!isMobile && !isTablet) || !userInfo" class="AdminUINavbar__left-side__full-logo">
            <!-- Logo completo (tablets y desktop) -->
            <CommonSvgFullLogo data-testid="admin-ui-navbar-full-logo" />
          </div>
          <div v-else class="AdminUINavbar__left-side__simple-logo">
            <!-- Logo simple de escudo (mobile) -->
            <CommonSvgSimpleLogo data-testid="admin-ui-navbar-simple-logo" />
          </div>
        </NuxtLink>
        <CommonUIEnvTag />
      </div>
      <div class="AdminUINavbar__right-side">
        <div class="AdminUINavbar__right-side__dark-mode-switch"></div>

        <div v-if="userInfo" class="AdminUINavbar__right-side__user">
          <div
            class="AdminUINavbar__right-side__user-avatar"
            data-testid="admin-ui-navbar-user-info"
            @click="toggleUserMenu"
          >
            <Avatar
              data-testid="admin-ui-navbar-user-avatar"
              :label="userName?.at(0)"
              shape="circle"
              aria-haspopup="true"
              aria-controls="overlay_menu"
              class="AdminUINavbar__right-side__user-avatar-icon"
            />
            <div class="AdminUINavbar__right-side__user-info">
              <span data-testid="admin-ui-navbar-user-name" class="AdminUINavbar__right-side__user-name">{{
                userName
              }}</span>
              <span
                data-testid="admin-ui-navbar-user-description"
                class="AdminUINavbar__right-side__user-description"
                >{{ userDescription }}</span
              >
            </div>
          </div>

          <Menu ref="userMenu" data-testid="admin-ui-navbar-user-menu" :model="userMenuItems" popup>
            <template #item="{ item, props }">
              <Button
                text
                :severity="item.severity || 'secondary'"
                v-bind="props.action"
                class="AdminUINavbar__user-menu__button"
              >
                <Icon
                  v-if="item.icon"
                  :style="{ color: item.severity === 'danger' ? 'var(--danger)' : 'inherit' }"
                  :name="item.icon"
                />
                {{ item.label }}
              </Button>
            </template>
          </Menu>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
  import { useToast } from 'primevue/usetoast';

  // composables
  const { logout, getUserInfo } = useAuth();
  const { isDesktop, isMobile, isTablet } = useDeviceScreen();
  const toast = useToast();

  // stores
  const isSidebarCollapsed = useState('isSidebarCollapsed', () => false);

  // refs
  const userMenu = ref();

  // computed
  const userInfo = getUserInfo();
  const userName = userInfo?.name as string;
  const userDescription = userInfo?.email as string;

  // functions
  function toggleSidebar() {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
  }

  async function handleLogout() {
    try {
      await logout();
    } catch {
      toast.add({
        severity: 'error',
        summary: 'Error al cerrar sesión',
        detail: 'No se pudo cerrar la sesión. Inténtalo de nuevo.',
        life: 5000,
      });
    }
  }

  function toggleUserMenu(event: Event) {
    userMenu?.value?.toggle(event);
  }

  // constants
  const userMenuItems = [
    // {
    //   label: 'Configuración',
    //   items: [
    //     {
    //       label: 'Modo claro/oscuro',
    //       icon: 'mdi:weather-sunny',
    //       command: toggleDarkMode,
    //       severity: 'secondary',
    //     },
    //   ],
    // },
    {
      label: 'Usuario',
      items: [
        {
          label: 'Cerrar sesión',
          icon: 'mdi:logout',
          command: handleLogout,
          severity: 'danger',
        },
      ],
    },
  ];
</script>

<style scoped>
  @reference '~/assets/css/main.css';

  .AdminUINavbar {
    @apply border-surface-600 bg-primary-1 sticky top-0 z-50 h-16 w-full border-b;
  }

  .AdminUINavbar__content {
    @apply flex h-full items-center justify-between gap-5 p-2.5 lg:px-5;
  }

  .AdminUINavbar__left-side {
    @apply flex min-w-0 items-center justify-start gap-5;
  }

  .AdminUINavbar__left-side__toggle-button {
    @apply flex shrink-0 cursor-pointer flex-col items-center justify-center rounded-full p-2.5;
  }

  .AdminUINavbar__left-side__logo-link {
    @apply shrink-0;
  }

  .AdminUINavbar__right-side {
    @apply flex items-center justify-end gap-5;
  }

  .AdminUINavbar__right-side__dark-mode-switch {
    @apply flex items-center justify-center gap-3;
  }

  .AdminUINavbar__right-side__user {
    @apply flex items-center justify-end gap-5;
  }

  .AdminUINavbar__right-side__user-avatar {
    @apply flex cursor-pointer items-center justify-center gap-2;
  }

  .AdminUINavbar__right-side__user-info {
    @apply flex flex-col items-start justify-center gap-1;
  }

  .AdminUINavbar__right-side__user-name {
    @apply text-surface-light text-base font-medium;
  }

  .AdminUINavbar__right-side__user-description {
    @apply text-surface-light text-sm;
  }

  .AdminUINavbar__right-side__user-avatar-icon {
    @apply bg-primary-2;
  }

  .AdminUINavbar__user-menu__button {
    @apply w-full! justify-start!;

    &:deep(*) {
      @apply text-left!;
    }

    &:deep(.p-button-label) {
      @apply flex-1! text-left!;
    }
  }
</style>
